import React, { useEffect, useState } from 'react'
import LoadingSpinner from './LoadingSpinner'
import { getApiUrl } from '../utils/api'
import {
  fetchPublicPaymentSettingsFromSupabase,
  fetchYocoServerCheckoutStatus,
  type PublicPaymentSettings,
} from '../services/paymentSupabase'

interface PaymentFormProps {
  totalAmount: number
  onPaymentSuccess: (paymentData: PaymentData) => void
  onPaymentError: (error: string) => void
  bookingDetails?: {
    roomName: string
    roomType?: string
    roomTypeId?: string
    checkIn: string
    checkOut: string
    guests: number
    baseRate?: number
    adminFee?: number
    breakageDeposit?: number
    confirmationNumber?: string
    guestName?: string
    guestEmail?: string
    guestPhone?: string
    specialRequests?: string
  }
}

interface PaymentData {
  transactionId: string
  amount: number
  method: string
  status: 'success' | 'failed' | 'pending'
  timestamp: string
}

const defaultPaymentSettings: PublicPaymentSettings = {
  yocoPublicKey: (import.meta as any).env?.VITE_YOCO_PUBLIC_KEY || '',
  eftBankName: 'Standard Bank',
  eftAccountName: 'Marina Glen Holiday Resort',
  eftAccountNumber: '084629451',
  eftBranchCode: '051001',
  eftAccountType: 'Business Cheque',
  eftReferencePrefix: 'MG',
  eftInstructions: 'Use your booking reference when making payment and upload proof of payment.'
}

const PaymentForm: React.FC<PaymentFormProps> = ({ 
  totalAmount, 
  onPaymentSuccess, 
  onPaymentError,
  bookingDetails 
}) => {
  const [paymentMethod, setPaymentMethod] = useState<'yoco' | 'eft'>('eft')
  const [loading, setLoading] = useState(false)
  const [paymentSettings, setPaymentSettings] = useState<PublicPaymentSettings>(defaultPaymentSettings)
  const [eftReference, setEftReference] = useState('')
  const [agreedToTerms, setAgreedToTerms] = useState(false)
  const [yocoServerCheckoutAvailable, setYocoServerCheckoutAvailable] = useState(false)

  const yocoCheckoutEnabled = yocoServerCheckoutAvailable && Boolean(paymentSettings.yocoPublicKey)

  useEffect(() => {
    const loadPaymentSettings = async () => {
      try {
        const [settings, checkoutStatus] = await Promise.all([
          fetchPublicPaymentSettingsFromSupabase(),
          fetchYocoServerCheckoutStatus().catch(() => ({
            available: false,
            publicKeyConfigured: false,
          })),
        ])

        setPaymentSettings(settings)
        setYocoServerCheckoutAvailable(checkoutStatus.available)
      } catch (error) {
        console.error('Failed to load payment settings:', error)
      }
    }

    loadPaymentSettings()
  }, [])

  useEffect(() => {
    if (bookingDetails?.confirmationNumber) {
      setEftReference(bookingDetails.confirmationNumber)
      return
    }

    if (!eftReference) {
      const prefix = (paymentSettings.eftReferencePrefix || 'MG').toUpperCase()
      setEftReference(`${prefix}${Date.now()}`)
    }
  }, [bookingDetails?.confirmationNumber, paymentSettings.eftReferencePrefix, eftReference])

  useEffect(() => {
    if (!yocoCheckoutEnabled && paymentMethod === 'yoco') {
      setPaymentMethod('eft')
    }
  }, [paymentMethod, yocoCheckoutEnabled])

  const processPayment = async () => {
    if (!agreedToTerms) {
      onPaymentError('Please accept the terms and conditions')
      return
    }

    setLoading(true)

    try {
      if (paymentMethod === 'yoco') {
        if (!yocoCheckoutEnabled) {
          onPaymentError('Card checkout is temporarily unavailable because the Yoco server settings are not fully configured.')
          return
        }

        if (!bookingDetails?.checkIn || !bookingDetails?.checkOut || !bookingDetails?.guestName || !bookingDetails?.guestEmail || !bookingDetails?.confirmationNumber) {
          onPaymentError('Missing booking details for Yoco checkout. Please restart your booking.')
          return
        }

        const nights = Math.max(1, Math.ceil((new Date(bookingDetails.checkOut).getTime() - new Date(bookingDetails.checkIn).getTime()) / (1000 * 3600 * 24)))
        const currentUrl = new URL(window.location.href)
        currentUrl.searchParams.delete('yocoReturn')
        currentUrl.searchParams.delete('accessToken')

        const checkoutResponse = await fetch(`${getApiUrl()}/api/payments/yoco-checkout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            amount: Math.round(totalAmount * 100),
            currency: 'ZAR',
            returnBaseUrl: currentUrl.toString(),
            metadata: {
              booking_reference: bookingDetails.confirmationNumber,
              room_name: bookingDetails.roomName,
              room_type: bookingDetails.roomType,
              room_type_id: bookingDetails.roomTypeId ? Number(bookingDetails.roomTypeId) : undefined,
              check_in: bookingDetails.checkIn,
              check_out: bookingDetails.checkOut,
              nights,
              guests_count: bookingDetails.guests,
              base_rate: bookingDetails.baseRate ?? Math.max(0, totalAmount / nights),
              admin_fee: bookingDetails.adminFee ?? 0,
              breakage_deposit: bookingDetails.breakageDeposit ?? 0,
              total_amount: totalAmount,
              guest_name: bookingDetails.guestName,
              guest_email: bookingDetails.guestEmail,
              guest_phone: bookingDetails.guestPhone,
            },
            lineItems: [
              {
                name: bookingDetails.roomName,
                quantity: 1,
                amount: Math.round(totalAmount * 100),
                currency: 'ZAR',
              }
            ]
          })
        })

        const checkoutResult = await checkoutResponse.json()
        if (!checkoutResponse.ok || !checkoutResult?.success || !checkoutResult?.data?.redirectUrl) {
          throw new Error(checkoutResult?.error || 'Failed to start YOCO checkout')
        }

        window.location.assign(checkoutResult.data.redirectUrl)
        return
      }

      if (paymentMethod === 'eft') {
        const paymentData: PaymentData = {
          transactionId: eftReference || `EFT-${Date.now()}`,
          amount: totalAmount,
          method: paymentMethod,
          status: 'pending',
          timestamp: new Date().toISOString()
        }
        onPaymentSuccess(paymentData)
        return
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'An error occurred while processing your payment. Please try again.'
      onPaymentError(message)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount: number) => {
    return `R${amount.toLocaleString()}`
  }

  if (loading) {
    return (
      <div style={{
        background: 'rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(20px)',
        borderRadius: '16px',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        padding: '3rem',
        textAlign: 'center'
      }}>
        <LoadingSpinner size="lg" text="Processing your payment..." />
        <p style={{
          color: 'rgba(255, 255, 255, 0.8)',
          fontSize: '0.9rem',
          textShadow: '1px 1px 2px rgba(0, 0, 0, 0.7)',
          marginTop: '1rem'
        }}>
          Please do not close this window or refresh the page.
        </p>
      </div>
    )
  }

  return (
    <div style={{
      background: 'rgba(255, 255, 255, 0.1)',
      backdropFilter: 'blur(20px)',
      borderRadius: '16px',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      padding: '2rem'
    }}>
      {/* Payment Summary */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.1)',
        borderRadius: '12px',
        padding: '1.5rem',
        marginBottom: '2rem',
        border: '1px solid rgba(255, 255, 255, 0.2)'
      }}>
        <h3 style={{
          color: 'white',
          fontSize: '1.25rem',
          fontWeight: '600',
          marginBottom: '1rem',
          textShadow: '2px 2px 4px rgba(0, 0, 0, 0.7)'
        }}>
          Payment Summary
        </h3>
        
        {bookingDetails && (
          <div className="space-y-2 mb-4">
            <div className="flex justify-between">
              <span style={{
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '0.9rem',
                textShadow: '1px 1px 2px rgba(0, 0, 0, 0.7)'
              }}>
                Accommodation:
              </span>
              <span style={{
                color: 'white',
                fontSize: '0.9rem',
                fontWeight: '500',
                textShadow: '1px 1px 2px rgba(0, 0, 0, 0.7)'
              }}>
                {bookingDetails.roomName}
              </span>
            </div>
            
            <div className="flex justify-between">
              <span style={{
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '0.9rem',
                textShadow: '1px 1px 2px rgba(0, 0, 0, 0.7)'
              }}>
                Dates:
              </span>
              <span style={{
                color: 'white',
                fontSize: '0.9rem',
                fontWeight: '500',
                textShadow: '1px 1px 2px rgba(0, 0, 0, 0.7)'
              }}>
                {new Date(bookingDetails.checkIn).toLocaleDateString()} - {new Date(bookingDetails.checkOut).toLocaleDateString()}
              </span>
            </div>
            
            <div className="flex justify-between">
              <span style={{
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '0.9rem',
                textShadow: '1px 1px 2px rgba(0, 0, 0, 0.7)'
              }}>
                Guests:
              </span>
              <span style={{
                color: 'white',
                fontSize: '0.9rem',
                fontWeight: '500',
                textShadow: '1px 1px 2px rgba(0, 0, 0, 0.7)'
              }}>
                {bookingDetails.guests} Guest{bookingDetails.guests > 1 ? 's' : ''}
              </span>
            </div>
          </div>
        )}
        
        <div className="border-t border-white border-opacity-20 pt-3">
          <div className="flex justify-between items-center">
            <span style={{
              color: 'white',
              fontSize: '1.2rem',
              fontWeight: '600',
              textShadow: '2px 2px 4px rgba(0, 0, 0, 0.7)'
            }}>
              Total Amount:
            </span>
            <span style={{
              color: '#0891b2',
              fontSize: '1.5rem',
              fontWeight: 'bold',
              textShadow: '2px 2px 4px rgba(0, 0, 0, 0.7)'
            }}>
              {formatCurrency(totalAmount)}
            </span>
          </div>
        </div>
      </div>

      {/* Payment Method Selection */}
      <div className="mb-6">
        <h4 style={{
          color: 'white',
          fontSize: '1.1rem',
          fontWeight: '600',
          marginBottom: '1rem',
          textShadow: '1px 1px 2px rgba(0, 0, 0, 0.7)'
        }}>
          Payment Method
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {[
            { id: 'yoco', label: 'YOCO Paygate', icon: '💳', popular: true },
            { id: 'eft', label: 'EFT Transfer', icon: '🏦', popular: false }
          ].map((method) => (
            <button
              key={method.id}
              onClick={() => {
                if (method.id === 'yoco' && !yocoCheckoutEnabled) {
                  return
                }

                setPaymentMethod(method.id as 'yoco' | 'eft')
              }}
              disabled={method.id === 'yoco' && !yocoCheckoutEnabled}
              style={{
                padding: '1rem',
                borderRadius: '12px',
                border: paymentMethod === method.id 
                  ? '2px solid #0891b2' 
                  : '1px solid rgba(255, 255, 255, 0.3)',
                background: paymentMethod === method.id 
                  ? 'rgba(8, 145, 178, 0.2)' 
                  : 'rgba(255, 255, 255, 0.1)',
                color: method.id === 'yoco' && !yocoCheckoutEnabled ? 'rgba(255, 255, 255, 0.5)' : 'white',
                backdropFilter: 'blur(8px)',
                cursor: method.id === 'yoco' && !yocoCheckoutEnabled ? 'not-allowed' : 'pointer',
                textAlign: 'center',
                transition: 'all 0.3s ease',
                position: 'relative',
                opacity: method.id === 'yoco' && !yocoCheckoutEnabled ? 0.6 : 1,
              }}
              className="hover:bg-white hover:bg-opacity-10"
            >
              {method.popular && (
                <div style={{
                  position: 'absolute',
                  top: '-8px',
                  right: '-8px',
                  background: '#f97316',
                  color: 'white',
                  fontSize: '0.7rem',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '8px',
                  fontWeight: '500'
                }}>
                  Recommended
                </div>
              )}
              <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>{method.icon}</div>
              <div style={{
                fontSize: '0.9rem',
                fontWeight: '500',
                textShadow: '1px 1px 2px rgba(0, 0, 0, 0.5)'
              }}>
                {method.label}
              </div>
              {method.id === 'yoco' && !yocoCheckoutEnabled && (
                <div style={{ marginTop: '0.4rem', fontSize: '0.72rem', lineHeight: 1.4 }}>
                  Server checkout unavailable
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Payment Details Form */}
      {paymentMethod === 'yoco' && (
        <div className="space-y-4 mb-6">
          <h4 style={{
            color: 'white',
            fontSize: '1.1rem',
            fontWeight: '600',
            marginBottom: '1rem',
            textShadow: '1px 1px 2px rgba(0, 0, 0, 0.7)'
          }}>
            YOCO Secure Checkout
          </h4>
          <div style={{
            background: 'rgba(8, 145, 178, 0.15)',
            borderRadius: '12px',
            padding: '1rem',
            border: '1px solid rgba(8, 145, 178, 0.35)'
          }}>
            <p style={{
              color: 'rgba(255, 255, 255, 0.92)',
              fontSize: '0.9rem',
              margin: 0,
              lineHeight: 1.5,
              textShadow: '1px 1px 2px rgba(0, 0, 0, 0.7)'
            }}>
              You will be redirected to the secure YOCO payment popup to complete your card payment.
            </p>
          </div>
          {!yocoCheckoutEnabled && (
            <div style={{
              background: 'rgba(245, 158, 11, 0.15)',
              borderRadius: '12px',
              padding: '1rem',
              border: '1px solid rgba(245, 158, 11, 0.35)'
            }}>
              <p style={{
                color: 'rgba(255, 255, 255, 0.92)',
                fontSize: '0.9rem',
                margin: 0,
                lineHeight: 1.5,
                textShadow: '1px 1px 2px rgba(0, 0, 0, 0.7)'
              }}>
                Card payments stay disabled until the Yoco checkout key and webhook settings are configured on the server.
              </p>
            </div>
          )}
        </div>
      )}

      {paymentMethod === 'eft' && (
        <div className="space-y-4 mb-6">
          <h4 style={{
            color: 'white',
            fontSize: '1.1rem',
            fontWeight: '600',
            marginBottom: '1rem',
            textShadow: '1px 1px 2px rgba(0, 0, 0, 0.7)'
          }}>
            EFT Transfer Details
          </h4>
          
          <div style={{
            background: 'rgba(249, 115, 22, 0.1)',
            borderRadius: '12px',
            padding: '1rem',
            border: '1px solid rgba(249, 115, 22, 0.3)',
            marginBottom: '1rem'
          }}>
            <h5 style={{
              color: '#f97316',
              fontSize: '0.9rem',
              fontWeight: '600',
              marginBottom: '0.5rem',
              textShadow: '1px 1px 2px rgba(0, 0, 0, 0.7)'
            }}>
              Our Banking Details
            </h5>
            <div style={{
              color: 'rgba(255, 255, 255, 0.9)',
              fontSize: '0.8rem',
              textShadow: '1px 1px 2px rgba(0, 0, 0, 0.7)',
              lineHeight: '1.4'
            }}>
              <p>Bank: {paymentSettings.eftBankName}</p>
              <p>Account Name: {paymentSettings.eftAccountName}</p>
              <p>Account Number: {paymentSettings.eftAccountNumber}</p>
              <p>Branch Code: {paymentSettings.eftBranchCode}</p>
              <p>Account Type: {paymentSettings.eftAccountType}</p>
            </div>
          </div>

          <div>
            <label style={{
              color: 'rgba(255, 255, 255, 0.8)',
              fontSize: '0.8rem',
              textShadow: '1px 1px 2px rgba(0, 0, 0, 0.7)',
              display: 'block',
              marginBottom: '0.5rem'
            }}>
              EFT Reference Number
            </label>
            <input
              type="text"
              value={eftReference}
              onChange={(e) => setEftReference(e.target.value)}
              placeholder="Use this reference on your transfer"
              style={{
                width: '100%',
                padding: '0.75rem',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '8px',
                color: 'white',
                fontSize: '0.9rem',
                backdropFilter: 'blur(8px)'
              }}
            />
          </div>
          
          {/* EFT form fields would go here */}
          <p style={{
            color: 'rgba(255, 255, 255, 0.8)',
            fontSize: '0.9rem',
            textShadow: '1px 1px 2px rgba(0, 0, 0, 0.7)',
            textAlign: 'center'
          }}>
            {paymentSettings.eftInstructions}
          </p>
          
          <div>
            <label style={{
              color: 'rgba(255, 255, 255, 0.8)',
              fontSize: '0.8rem',
              textShadow: '1px 1px 2px rgba(0, 0, 0, 0.7)',
              display: 'block',
              marginBottom: '0.5rem'
            }}>
              Upload Proof of Payment
            </label>
            <input
              type="file"
              accept="image/*,.pdf"
              style={{
                width: '100%',
                padding: '0.75rem',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '8px',
                color: 'white',
                fontSize: '0.9rem',
                backdropFilter: 'blur(8px)'
              }}
            />
          </div>
        </div>
      )}

      {/* Security & Terms */}
      <div className="mb-6">
        <div style={{
          background: 'rgba(16, 185, 129, 0.1)',
          borderRadius: '12px',
          padding: '1rem',
          border: '1px solid rgba(16, 185, 129, 0.3)',
          marginBottom: '1rem'
        }}>
          <div className="flex items-center">
            <span style={{ fontSize: '1.2rem', marginRight: '0.5rem' }}>🔒</span>
            <div>
              <h5 style={{
                color: '#10B981',
                fontSize: '0.9rem',
                fontWeight: '600',
                marginBottom: '0.25rem',
                textShadow: '1px 1px 2px rgba(0, 0, 0, 0.7)'
              }}>
                Secure Payment
              </h5>
              <p style={{
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '0.8rem',
                textShadow: '1px 1px 2px rgba(0, 0, 0, 0.7)',
                margin: 0
              }}>
                Your payment information is encrypted and secure. We never store your card details.
              </p>
            </div>
          </div>
        </div>
        
        <div className="flex items-start">
          <input
            type="checkbox"
            id="terms"
            checked={agreedToTerms}
            onChange={(e) => setAgreedToTerms(e.target.checked)}
            style={{ marginRight: '0.75rem', marginTop: '0.25rem' }}
          />
          <label htmlFor="terms" style={{
            color: 'rgba(255, 255, 255, 0.8)',
            fontSize: '0.8rem',
            textShadow: '1px 1px 2px rgba(0, 0, 0, 0.7)',
            lineHeight: '1.4'
          }}>
            I agree to the{' '}
            <span style={{ color: '#0891b2', textDecoration: 'underline', cursor: 'pointer' }}>
              Terms & Conditions
            </span>{' '}
            and{' '}
            <span style={{ color: '#0891b2', textDecoration: 'underline', cursor: 'pointer' }}>
              Privacy Policy
            </span>. 
            I understand that this payment is non-refundable except as outlined in the cancellation policy.
          </label>
        </div>
      </div>

      {/* Payment Button */}
      <button
        onClick={processPayment}
        disabled={!agreedToTerms || (paymentMethod === 'yoco' && !yocoCheckoutEnabled)}
        style={{
          width: '100%',
          padding: '1rem 2rem',
          background: !agreedToTerms || (paymentMethod === 'yoco' && !yocoCheckoutEnabled)
            ? 'rgba(107, 114, 128, 0.5)'
            : 'linear-gradient(135deg, #0891b2 0%, #0e7490 100%)',
          color: 'white',
          border: 'none',
          borderRadius: '12px',
          fontSize: '1.1rem',
          fontWeight: '600',
          cursor: !agreedToTerms || (paymentMethod === 'yoco' && !yocoCheckoutEnabled) ? 'not-allowed' : 'pointer',
          transition: 'all 0.3s ease',
          textShadow: '1px 1px 2px rgba(0, 0, 0, 0.5)',
          boxShadow: '0 4px 16px rgba(8, 145, 178, 0.3)'
        }}
        className={!agreedToTerms || (paymentMethod === 'yoco' && !yocoCheckoutEnabled) ? '' : 'hover:shadow-lg transform hover:scale-105'}
      >
        {paymentMethod === 'yoco'
          ? `Pay with YOCO - ${formatCurrency(totalAmount)}`
          : `Submit EFT Payment - ${formatCurrency(totalAmount)}`
        }
      </button>
      
      <p style={{
        color: 'rgba(255, 255, 255, 0.6)',
        fontSize: '0.7rem',
        textShadow: '1px 1px 2px rgba(0, 0, 0, 0.7)',
        textAlign: 'center',
        marginTop: '1rem'
      }}>
        Powered by Marina Glen Secure Payment System
      </p>
    </div>
  )
}

export default PaymentForm